import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

/**
 * Build a single "booklet" PDF that combines the dispute letter PDF with all
 * supporting documents uploaded for a chargeback case.
 *
 * Supported attachment types:
 *  - PDF → all pages are appended (via PDFDocument.copyPages)
 *  - PNG / JPEG / JPG → embedded as a full-page image (aspect ratio preserved)
 *  - Anything else → a small placeholder page that names the file and notes
 *    it is included by reference (so the booklet still records its existence)
 *
 * @param {object} params
 * @param {Blob}   params.letterBlob          The dispute letter PDF blob (required).
 * @param {Array<{key: string, name?: string}>} params.attachments  Supporting docs to append.
 * @param {(att: {key:string, name?:string}) => Promise<{blob: Blob, contentType?: string, fileName?: string}>} params.fetchAttachment
 *                                              Caller-supplied fetcher (handles auth, base URL, etc.).
 * @param {(progress: {index:number, total:number, name?:string, status:string}) => void} [params.onProgress]
 *
 * @returns {Promise<{blob: Blob, filename: string, includedCount: number, skippedCount: number}>}
 */
export async function buildChargebackBooklet({
  letterBlob,
  attachments = [],
  fetchAttachment,
  onProgress,
  baseFilename = 'Chargeback_Booklet',
}) {
  if (!letterBlob) {
    throw new Error('Letter PDF blob is required to build booklet.');
  }
  if (typeof fetchAttachment !== 'function') {
    throw new Error('fetchAttachment(attachment) callback is required.');
  }

  const letterBytes = new Uint8Array(await letterBlob.arrayBuffer());
  const merged = await PDFDocument.load(letterBytes);

  const total = attachments.length;
  let includedCount = 0;
  let skippedCount = 0;

  // Lazy-load fonts once for divider / placeholder pages.
  const helvetica = await merged.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await merged.embedFont(StandardFonts.HelveticaBold);

  for (let i = 0; i < total; i += 1) {
    const att = attachments[i] || {};
    const label = att.name || `Attachment ${i + 1}`;
    const indexLabel = `Attachment ${i + 1} of ${total}`;

    onProgress?.({ index: i, total, name: label, status: 'fetching' });

    let fetched = null;
    try {
      fetched = await fetchAttachment(att);
    } catch (err) {
      console.warn(`Booklet: failed to fetch "${label}":`, err);
    }

    if (!fetched?.blob) {
      addPlaceholderPage(merged, helvetica, helveticaBold, indexLabel, label, 'Could not load this attachment.');
      skippedCount += 1;
      onProgress?.({ index: i, total, name: label, status: 'skipped' });
      continue;
    }

    const detected = detectAttachmentKind(fetched, label, att);

    try {
      if (detected.kind === 'pdf') {
        // PDFs keep their own structure — append pages directly, no extra divider page.
        await appendPdfPages(merged, fetched.blob);
        includedCount += 1;
        onProgress?.({ index: i, total, name: label, status: 'included-pdf' });
      } else if (detected.kind === 'image') {
        // Image goes on its own page, with a small attachment header strip on the same page.
        await appendImagePage(
          merged,
          fetched.blob,
          detected.imageType,
          helvetica,
          helveticaBold,
          indexLabel,
          label
        );
        includedCount += 1;
        onProgress?.({ index: i, total, name: label, status: 'included-image' });
      } else {
        addPlaceholderPage(
          merged,
          helvetica,
          helveticaBold,
          indexLabel,
          label,
          'This attachment format cannot be embedded inline. The original file is available in case history.'
        );
        skippedCount += 1;
        onProgress?.({ index: i, total, name: label, status: 'placeholder' });
      }
    } catch (err) {
      console.warn(`Booklet: failed to embed "${label}":`, err);
      addPlaceholderPage(
        merged,
        helvetica,
        helveticaBold,
        indexLabel,
        label,
        'The attachment could not be rendered in the booklet. The original file is available in case history.'
      );
      skippedCount += 1;
      onProgress?.({ index: i, total, name: label, status: 'placeholder' });
    }
  }

  const mergedBytes = await merged.save();
  const blob = new Blob([mergedBytes], { type: 'application/pdf' });
  const filename = `${baseFilename}_${new Date().toISOString().split('T')[0]}.pdf`;

  return { blob, filename, includedCount, skippedCount };
}

function detectAttachmentKind(fetched, label, att) {
  const ct = String(fetched.contentType || '').toLowerCase();
  const name = String(
    fetched.fileName || att.name || label || ''
  ).toLowerCase();

  if (ct === 'application/pdf' || name.endsWith('.pdf')) {
    return { kind: 'pdf' };
  }
  if (ct === 'image/png' || name.endsWith('.png')) {
    return { kind: 'image', imageType: 'png' };
  }
  if (
    ct === 'image/jpeg' ||
    ct === 'image/jpg' ||
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg')
  ) {
    return { kind: 'image', imageType: 'jpg' };
  }
  return { kind: 'other' };
}

async function appendPdfPages(targetDoc, pdfBlob) {
  const bytes = new Uint8Array(await pdfBlob.arrayBuffer());
  // ignoreEncryption lets pdf-lib at least try to load lightly-protected docs.
  const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
  const pages = await targetDoc.copyPages(src, src.getPageIndices());
  pages.forEach((p) => targetDoc.addPage(p));
}

async function appendImagePage(
  targetDoc,
  imageBlob,
  imageType,
  font,
  boldFont,
  indexLabel,
  name
) {
  const bytes = new Uint8Array(await imageBlob.arrayBuffer());
  const image =
    imageType === 'png'
      ? await targetDoc.embedPng(bytes)
      : await targetDoc.embedJpg(bytes);

  // US Letter portrait by default.
  const pageWidth = 612;
  const pageHeight = 792;
  const margin = 36;
  const headerHeight = 52; // small strip above the image, on the same page

  const page = targetDoc.addPage([pageWidth, pageHeight]);

  drawAttachmentHeader(page, font, boldFont, indexLabel, name, pageWidth, pageHeight, headerHeight);

  const maxW = pageWidth - margin * 2;
  const maxH = pageHeight - margin * 2 - headerHeight - 12; // 12pt breathing room below header
  const scale = Math.min(maxW / image.width, maxH / image.height, 1);
  const w = image.width * scale;
  const h = image.height * scale;
  const x = (pageWidth - w) / 2;
  // Top-align under the header so a small image doesn't float in the middle of empty space.
  const y = pageHeight - headerHeight - 12 - h;
  page.drawImage(image, { x, y, width: w, height: h });
}

function addPlaceholderPage(targetDoc, font, boldFont, indexLabel, name, message) {
  const pageWidth = 612;
  const pageHeight = 792;
  const headerHeight = 52;
  const page = targetDoc.addPage([pageWidth, pageHeight]);

  drawAttachmentHeader(page, font, boldFont, indexLabel, name, pageWidth, pageHeight, headerHeight);

  const wrappedMsg = wrapText(message, 80);
  let y = pageHeight - headerHeight - 40;
  page.drawText('Attachment included by reference', {
    x: 36,
    y,
    size: 13,
    font: boldFont,
    color: rgb(0.11, 0.16, 0.24),
  });
  y -= 22;

  wrappedMsg.forEach((line) => {
    page.drawText(line, {
      x: 36,
      y,
      size: 11,
      font,
      color: rgb(0.4, 0.45, 0.5),
    });
    y -= 16;
  });
}

/**
 * Small attachment header strip drawn directly on the same page as the image
 * (or placeholder). Replaces the previous standalone divider page.
 */
function drawAttachmentHeader(page, font, boldFont, indexLabel, name, pageWidth, pageHeight, headerHeight) {
  page.drawRectangle({
    x: 0,
    y: pageHeight - headerHeight,
    width: pageWidth,
    height: headerHeight,
    color: rgb(0.13, 0.77, 0.37),
  });

  // Truncate filename if needed so it fits beside the index label on one line.
  const safeName = String(name || 'Attachment').replace(/\s+/g, ' ').trim();
  const display = safeName.length > 80 ? `${safeName.slice(0, 77)}…` : safeName;

  // Padding from the top of the strip; both baselines must stay above
  // (pageHeight - headerHeight) by at least the font's descender depth,
  // otherwise glyphs like 'p'/'g' clip out of the green band.
  page.drawText(indexLabel, {
    x: 36,
    y: pageHeight - 20,
    size: 11,
    font: boldFont,
    color: rgb(1, 1, 1),
  });
  page.drawText(display, {
    x: 36,
    y: pageHeight - 38,
    size: 10,
    font,
    color: rgb(1, 1, 1),
  });
}

function wrapText(text, charsPerLine) {
  const words = String(text || '').split(/\s+/);
  const lines = [];
  let current = '';
  words.forEach((w) => {
    if (!w) return;
    if ((current + ' ' + w).trim().length > charsPerLine) {
      if (current) lines.push(current.trim());
      current = w;
    } else {
      current = (current + ' ' + w).trim();
    }
  });
  if (current) lines.push(current);
  return lines.length ? lines : [''];
}

/**
 * Extract supporting-document attachments from a chargeback history payload.
 * Mirrors the same ACTION filter that buildEvidenceFromRawHistoryEntries uses.
 * @param {Array} entries Raw history rows from /chargebacks/{tenant}/{id}/history/
 * @returns {Array<{key: string, name: string, url?: string}>}
 */
export function extractSupportingDocumentsFromHistory(entries) {
  const seen = new Map();
  for (const item of entries || []) {
    const action = item.ACTION || item.action;
    if (!['FILE_UPLOAD', 'DOCUMENT_UPLOADED'].includes(action)) continue;
    const url = item.FILE_LOCATION || item.file_location;
    if (!url) continue;
    const key = extractS3KeyFromUrl(url);
    if (!key) continue;
    if (!seen.has(key)) {
      seen.set(key, {
        key,
        url,
        name: item.FILE_NAME || item.file_name || 'Supporting document',
      });
    }
  }
  return Array.from(seen.values());
}

function extractS3KeyFromUrl(fileLocation) {
  if (!fileLocation || typeof fileLocation !== 'string') return null;
  const trimmed = fileLocation.trim();
  if (!trimmed) return null;
  if (!/^https?:\/\//i.test(trimmed)) {
    return trimmed.replace(/^\/+/, '');
  }
  try {
    const u = new URL(trimmed);
    let path = u.pathname.startsWith('/') ? u.pathname.slice(1) : u.pathname;
    if (/^s3[.-]/i.test(u.hostname) && u.hostname.includes('amazonaws.com')) {
      const parts = path.split('/').filter(Boolean);
      if (parts.length > 1) return parts.slice(1).join('/');
    }
    return path || null;
  } catch {
    return null;
  }
}
